import type {ZodType} from "zod";
import {z as zod} from "zod";
import type {Integer, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {integerZodValidator, iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {uniqueUuid} from "@vesta/unique/unique";

export enum MediaLibraryDirentType {
    audio = "0ba0ca04-0b92-4478-ad84-c58c2f3c4fdc",
    document = "7d8e9f0a-1b2c-3d4e-5f6a-7b8c9d0e1f2a",
    folder = "5b54b1e1-35c8-494a-a9d5-648af385eacc",
    image = "15c6f642-6fc2-4d4e-9d91-5feb55ffe90a",
    pdf = "3ba4aed5-8708-45a6-a047-35749eb7c7b2",
    unknown = "2ee597d1-4b3f-4574-8032-1e112db3322c",
    video = "46cf7098-f494-431f-a55f-fed9cb09cf90",
}

export enum MediaLibraryDirentStatus {
    active = "c4a8e1f2-3b5d-4e6a-9c0d-1e2f3a4b5c6d",
    archived = "e6c0a3b4-5d7f-6a8b-1e2f-3a4b5c6d7e8f",
    deleted = "d5b9f2a3-4c6e-5f7b-0d1e-2f3a4b5c6d7e",
}

export type MediaLibraryDirentUserMetadata = {
    altText?: string;
    description?: string;
    /** Static conversion: source asset this file was derived from. */
    derivedFromDirentId?: Uuid;
    /** @deprecated Use `description` instead. Kept for reading legacy rows. */
    title?: string;
};

export type MediaLibraryDirent = {
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    id: Uuid;
    /**
     * Folder rollup only: count of descendant files (nested folders included in the walk, but not counted).
     */
    itemCount?: Integer;
    name: string;
    /**
     * Computed: count of processed `originHit` rows in `media_library_dirent_events`.
     * Folders stay at 0 / omitted.
     */
    originHitCount?: Integer;
    parentFolderId: Uuid | null;
    status: MediaLibraryDirentStatus;
    /**
     * Folder rollup only: sum of `fileSize` across all descendant files.
     */
    totalSizeBytes?: Integer;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
} & (
    | {
        data: MediaLibraryDirentUserMetadata & {
            fileSize: Integer;
            mimeType: string;
        };
        type: MediaLibraryDirentType.audio;
      }
    | {
        data: MediaLibraryDirentUserMetadata | null;
        type: MediaLibraryDirentType.folder;
      }
    | {
        data: MediaLibraryDirentUserMetadata & {
            fileSize: Integer;
            format: string;
            height: Integer;
            mimeType: string;
            width: Integer;
        };
        type: MediaLibraryDirentType.image;
      }
    | {
        data: MediaLibraryDirentUserMetadata & {
            fileSize: Integer;
            mimeType: string;
        };
        type: MediaLibraryDirentType.document;
      }
    | {
        data: MediaLibraryDirentUserMetadata & {
            fileSize: Integer;
            mimeType: string;
        };
        type: MediaLibraryDirentType.pdf;
      }
    | {
        data: MediaLibraryDirentUserMetadata & {
            fileSize: Integer;
            mimeType: string;
        };
        type: MediaLibraryDirentType.unknown;
      }
    | {
        data: MediaLibraryDirentUserMetadata & {
            fileSize: Integer;
            mimeType: string;
        };
        type: MediaLibraryDirentType.video;
      }
);

export type MediaLibraryDirentWithPath = MediaLibraryDirent & {
    path: string;
};

const mediaLibraryDirentUserMetadataZodValidator = {
    altText: zod.string().optional(),
    derivedFromDirentId: uuidZodValidator.optional(),
    description: zod.string().optional(),
    title: zod.string().optional(),
};

const mediaLibraryDirentDataZodValidator = zod.union([
    zod.object({
        data: zod.object({
            fileSize: integerZodValidator,
            mimeType: zod.string(),
            ...mediaLibraryDirentUserMetadataZodValidator,
        }),
        type: zod.literal(MediaLibraryDirentType.audio),
    }),
    zod.object({
        data: zod.union([
            zod.null(),
            zod.object(mediaLibraryDirentUserMetadataZodValidator),
        ]),
        type: zod.literal(MediaLibraryDirentType.folder),
    }),
    zod.object({
        data: zod.object({
            fileSize: integerZodValidator,
            format: zod.string(),
            height: integerZodValidator,
            mimeType: zod.string(),
            width: integerZodValidator,
            ...mediaLibraryDirentUserMetadataZodValidator,
        }),
        type: zod.literal(MediaLibraryDirentType.image),
    }),
    zod.object({
        data: zod.object({
            fileSize: integerZodValidator,
            mimeType: zod.string(),
            ...mediaLibraryDirentUserMetadataZodValidator,
        }),
        type: zod.literal(MediaLibraryDirentType.document),
    }),
    zod.object({
        data: zod.object({
            fileSize: integerZodValidator,
            mimeType: zod.string(),
            ...mediaLibraryDirentUserMetadataZodValidator,
        }),
        type: zod.literal(MediaLibraryDirentType.pdf),
    }),
    zod.object({
        data: zod.object({
            fileSize: integerZodValidator,
            mimeType: zod.string(),
            ...mediaLibraryDirentUserMetadataZodValidator,
        }),
        type: zod.literal(MediaLibraryDirentType.unknown),
    }),
    zod.object({
        data: zod.object({
            fileSize: integerZodValidator,
            mimeType: zod.string(),
            ...mediaLibraryDirentUserMetadataZodValidator,
        }),
        type: zod.literal(MediaLibraryDirentType.video),
    }),
]);

export const mediaLibraryDirentZodValidator: ZodType<MediaLibraryDirent> = zod
    .object({
        createdAt: iso8601DateTimeZodValidator,
        createdBy: uuidZodValidator,
        id: uuidZodValidator,
        itemCount: integerZodValidator.optional(),
        name: zod.string(),
        originHitCount: integerZodValidator.optional(),
        parentFolderId: uuidZodValidator.nullable(),
        status: zod.enum(MediaLibraryDirentStatus),
        totalSizeBytes: integerZodValidator.optional(),
        updatedAt: iso8601DateTimeZodValidator,
        updatedBy: uuidZodValidator,
    })
    .and(mediaLibraryDirentDataZodValidator);

export const mediaLibraryDirentWithPathZodValidator: ZodType<MediaLibraryDirentWithPath> =
    mediaLibraryDirentZodValidator.and(
        zod.object({
            path: zod.string(),
        }),
    );

export const ROOT_DIRENT_ID = uniqueUuid`e4be8d27-9c23-4549-8b52-579ec55d6272`;
export const ROOT_DIRENT_NAME = "/";
